import { api } from '../lib/api';
import type { EntityDocument, TiptapDoc } from '../types/entities';
import type { SceneSegment, SceneConflict, SceneSegmentComment, SceneSegmentField, SceneSegmentVersion } from '../types/scenes';
import type { StoryTimePlacement } from '../types/timeline';
import type { CorrectionRequestOptions, SegmentCorrectionPreview } from '../types/correction';
import { streamCorrectionPreview } from '../lib/correctionStream';
import type { SceneSaveConflict, SceneSaveResolution } from '../types/sceneSaveConflict';

type WorkspaceSceneEditStore = {
  activeEntity: EntityDocument | null;
  sceneSegments: SceneSegment[];
  sceneSegmentFields: SceneSegmentField[];
  sceneConflicts: SceneConflict[];
  loadSceneSegments(sceneId?: string, entityVersionId?: string | null, entityRevisionId?: string | null): Promise<void>;
  loadSceneConflicts(sceneId?: string): Promise<void>;
  refreshActiveVersionMetadata(entityId?: string): Promise<void>;
  loadCharacterTimeline(): Promise<void>;
  scheduleStoryTimelineRefresh(): void;
  setSceneSegmentVersionComments(versionId: string, comments: SceneSegmentComment[]): void;
  loadSceneSegmentContent(versionId: string): Promise<Pick<SceneSegmentVersion, 'contentDoc' | 'fieldValues'>>;
};

const sceneCommentRequests = new WeakMap<SceneSegmentVersion, Promise<void>>();
const sceneContentRequests = new WeakMap<SceneSegmentVersion, Promise<Pick<SceneSegmentVersion, 'contentDoc' | 'fieldValues'>>>();
const sceneCommentGenerations = new WeakMap<SceneSegmentVersion, number>();

export const workspaceSceneEditActions = {
  async loadSceneSaveConflict(versionId: string) {
    return api<SceneSaveConflict>(`/api/v1/scene-segment-versions/${versionId}/save-conflict`);
  },
  async resolveSceneSaveConflict(this: WorkspaceSceneEditStore, versionId: string, input: SceneSaveResolution) {
    const result = await api<{ item: SceneSegmentVersion }>(`/api/v1/scene-segment-versions/${versionId}/save-conflict/resolve`, { method: 'POST', body: JSON.stringify(input) });
    await this.loadSceneSegments(); await this.refreshActiveVersionMetadata(); this.scheduleStoryTimelineRefresh();
    return result.item;
  },
  async previewSegmentCorrection(versionId: string, baseContentDoc: TiptapDoc, options: CorrectionRequestOptions = {}) {
    if (options.onProgress) return streamCorrectionPreview(versionId, baseContentDoc, options);
    return api<SegmentCorrectionPreview>(`/api/v1/scene-segment-versions/${versionId}/correction/preview`, { method: 'POST', signal: options.signal, body: JSON.stringify({ baseContentDoc, reasoningEffort: options.reasoningEffort ?? 'low', senfLevel: options.senfLevel }) });
  },
  async acceptSegmentCorrection(this: WorkspaceSceneEditStore, preview: SegmentCorrectionPreview) {
    const { correctionId, baseContentDoc, activeVersionId, activeVersionHash } = preview;
    const edits = preview.edits.map(({ nodeIndex, before, after, occurrence, reason, from, to }) => ({ nodeIndex, before, after, occurrence, reason, from, to }));
    const result = await api<{ item: SceneSegmentVersion }>(`/api/v1/scene-segment-versions/${preview.sourceVersionId}/correction/accept`, {
      method: 'POST', body: JSON.stringify({ correctionId, baseContentDoc, edits, activeVersionId, activeVersionHash })
    });
    if (this.sceneSegments.some((segment) => segment.versions.some((version) => version.id === preview.sourceVersionId))) {
      await this.loadSceneSegments(); await this.refreshActiveVersionMetadata();
    }
    this.scheduleStoryTimelineRefresh();
    return result.item;
  },
  async loadSceneSegments(this: WorkspaceSceneEditStore, sceneId?: string, entityVersionId?: string | null, entityRevisionId?: string | null) {
    const id = sceneId ?? this.activeEntity?.id;
    if (!id) { this.sceneSegments = []; return; }
    const entity = this.activeEntity;
    const params = new URLSearchParams();
    if (entityVersionId) params.set('entityVersionId', entityVersionId);
    if (entityRevisionId) params.set('entityRevisionId', entityRevisionId);
    const query = params.size ? `?${params.toString()}` : '';
    const response = await api<{ items: SceneSegment[]; fields: SceneSegmentField[] }>(`/api/v1/entities/${id}/scene-segments${query}`);
    if (this.activeEntity === entity && entity?.id === id) { this.sceneSegments = response.items; this.sceneSegmentFields = response.fields; }
  },
  async loadSceneConflicts(this: WorkspaceSceneEditStore, sceneId?: string) {
    const id = sceneId ?? this.activeEntity?.id;
    if (!id) { this.sceneConflicts = []; return; }
    const entity = this.activeEntity;
    const response = await api<{ items: SceneConflict[] }>(`/api/v1/entities/${id}/scene-conflicts`);
    if (this.activeEntity === entity && entity?.id === id) this.sceneConflicts = response.items;
  },
  async acknowledgeSceneConflict(this: WorkspaceSceneEditStore, otherSceneId: string) {
    if (!this.activeEntity) return;
    await api(`/api/v1/entities/${this.activeEntity.id}/scene-conflicts/${otherSceneId}/acknowledge`, { method: 'POST' });
    await this.loadSceneConflicts();
  },
  async createSceneSegment(this: WorkspaceSceneEditStore) {
    if (!this.activeEntity) return;
    await api(`/api/v1/entities/${this.activeEntity.id}/scene-segments`, { method: 'POST', body: JSON.stringify({}) });
    await this.loadSceneSegments();
    await this.refreshActiveVersionMetadata();
    this.scheduleStoryTimelineRefresh();
  },
  async updateSceneSegment(this: WorkspaceSceneEditStore, id: string, patch: {
    label?: string; contentDoc?: TiptapDoc; fieldValues?: Record<string, unknown>; sortOrder?: number;
    baseLabel?: string; baseFieldValues?: Record<string, unknown>
  }) {
    const segment = this.sceneSegments.find((item) => item.id === id);
    const payload = {
      ...(patch.label !== undefined && segment ? { baseLabel: segment.label } : {}),
      ...(patch.fieldValues !== undefined && segment ? { baseFieldValues: segment.fieldValues } : {}),
      ...patch
    };
    await api(`/api/v1/scene-segments/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
    await this.loadSceneSegments();
    await this.refreshActiveVersionMetadata();
    if (this.activeEntity?.templateCategory === 'szene') await this.loadSceneConflicts();
    this.scheduleStoryTimelineRefresh();
  },
  async updateSceneSegmentTimePlacement(this: WorkspaceSceneEditStore, id: string, placement: StoryTimePlacement) {
    await api(`/api/v1/scene-segments/${id}/time-placement`, { method: 'PATCH', body: JSON.stringify(placement) });
    await this.loadSceneSegments();
    await this.refreshActiveVersionMetadata();
    if (this.activeEntity?.templateCategory === 'szene') await this.loadSceneConflicts();
    this.scheduleStoryTimelineRefresh();
  },
  async updateTimelineSegmentTimePlacement(this: WorkspaceSceneEditStore, id: string, placement: Partial<StoryTimePlacement>) {
    await api(`/api/v1/scene-segments/${id}/time-placement`, { method: 'PATCH', body: JSON.stringify(placement) });
    await this.loadCharacterTimeline();
  },
  async fillMissingTimelineSegmentTimes(this: WorkspaceSceneEditStore, steps: Array<{
    segmentId: string; timeSource: 'inherit' | 'relative'; relativeAnchor: 'scene-start' | 'previous-segment-end' | null
  }>) {
    try {
      for (const step of steps) await api(`/api/v1/scene-segments/${step.segmentId}/time-placement`, {
        method: 'PATCH', body: JSON.stringify({ timeSource: step.timeSource, relativeAnchor: step.relativeAnchor, relativeOffsetMinutes: 0, onlyIfMissing: true })
      });
    } finally {
      await this.loadCharacterTimeline();
    }
  },
  async updateTimelineSegmentDuration(this: WorkspaceSceneEditStore, id: string, durationMinutes: number | null) {
    await api(`/api/v1/scene-segments/${id}/duration`, { method: 'PATCH', body: JSON.stringify({ durationMinutes }) });
    await this.loadCharacterTimeline();
  },
  async createSceneSegmentVersion(this: WorkspaceSceneEditStore, segmentId: string, sourceVersionId?: string | null, selectedCommentIds: string[] = [], recovery?: { contentDoc: TiptapDoc; fieldValues?: Record<string, unknown> }) {
    const result = await api<{ item: SceneSegmentVersion }>(`/api/v1/scene-segments/${segmentId}/versions`, { method: 'POST', body: JSON.stringify({ sourceVersionId: sourceVersionId ?? null, activate: !recovery, selectedCommentIds, ...(recovery ? { ...recovery, name: `Wiederherstellung · ${new Date().toLocaleString('de-DE')}` } : {}) }) });
    await this.loadSceneSegments();
    await this.refreshActiveVersionMetadata();
    this.scheduleStoryTimelineRefresh();
    return result.item;
  },
  async updateSceneSegmentVersion(this: WorkspaceSceneEditStore, versionId: string, patch: {
    name?: string; contentDoc?: TiptapDoc; fieldValues?: Record<string, unknown>;
    baseContentDoc?: TiptapDoc; baseFieldValues?: Record<string, unknown>
  }) {
    const version = this.sceneSegments.flatMap((segment) => segment.versions).find((item) => item.id === versionId);
    if (version) sceneContentRequests.delete(version);
    const baseline = patch.contentDoc !== undefined && version ? await this.loadSceneSegmentContent(versionId) : undefined;
    const payload = JSON.parse(JSON.stringify({
      ...(baseline ? { baseContentDoc: baseline.contentDoc } : {}),
      ...(patch.fieldValues !== undefined && version ? { baseFieldValues: version.fieldValues } : {}),
      ...patch
    }));
    const result = await api<{ item: SceneSegmentVersion }>(`/api/v1/scene-segment-versions/${versionId}`, { method: 'PATCH', body: JSON.stringify(payload) });
    const currentSegment = this.sceneSegments.find((item) => item.versions.some((candidate) => candidate.id === versionId));
    const currentVersion = currentSegment?.versions.find((item) => item.id === versionId);
    if (currentVersion) {
      Object.assign(currentVersion, {
        contentDoc: result.item.contentDoc, fieldValues: result.item.fieldValues,
        name: result.item.name, updatedAt: result.item.updatedAt, contentLoaded: true
      });
      if (currentVersion.status === 'active' && currentSegment) {
        currentSegment.contentDoc = result.item.contentDoc;
        currentSegment.fieldValues = result.item.fieldValues;
      }
    }
    await this.refreshActiveVersionMetadata();
    this.scheduleStoryTimelineRefresh();
  },
  async activateSceneSegmentVersion(this: WorkspaceSceneEditStore, versionId: string) {
    await api(`/api/v1/scene-segment-versions/${versionId}/activate`, { method: 'POST' });
    await this.loadSceneSegments();
    await this.refreshActiveVersionMetadata();
    if (this.activeEntity?.templateCategory === 'szene') await this.loadSceneConflicts();
    this.scheduleStoryTimelineRefresh();
  },
  async archiveSceneSegmentVersion(this: WorkspaceSceneEditStore, versionId: string) {
    await api(`/api/v1/scene-segment-versions/${versionId}/archive`, { method: 'POST' });
    await this.loadSceneSegments();
    await this.refreshActiveVersionMetadata();
    this.scheduleStoryTimelineRefresh();
  },
  async deleteSceneSegmentVersion(this: WorkspaceSceneEditStore, versionId: string) {
    await api(`/api/v1/scene-segment-versions/${versionId}`, { method: 'DELETE' });
    await this.loadSceneSegments();
    await this.refreshActiveVersionMetadata();
    if (this.activeEntity?.templateCategory === 'szene') await this.loadSceneConflicts();
    this.scheduleStoryTimelineRefresh();
  },
  async compareSceneSegmentVersions(this: WorkspaceSceneEditStore, segmentId: string, leftVersionId: string, rightVersionId: string, model: 'luna' | 'terra' | 'sol' = 'luna', mode: 'compare' | 'feedback' = 'compare', includeRewrite = false, customPrompt = '') {
    return api<{ analysis: string; model: string; leftVersionId: string; rightVersionId: string; usage: { inputTokens: number; outputTokens: number; totalTokens: number } | null }>(
      `/api/v1/scene-segments/${segmentId}/versions/compare`,
      { method: 'POST', body: JSON.stringify({ leftVersionId, rightVersionId, model, mode, includeRewrite, ...(customPrompt.trim() ? { customPrompt: customPrompt.trim() } : {}) }) }
    );
  },
  setSceneSegmentVersionComments(this: WorkspaceSceneEditStore, versionId: string, comments: SceneSegmentComment[]) {
    const version = this.sceneSegments.flatMap((segment) => segment.versions).find((item) => item.id === versionId);
    if (!version) return;
    sceneCommentGenerations.set(version, (sceneCommentGenerations.get(version) ?? 0) + 1);
    version.comments = comments;
    version.commentsLoaded = true;
    version.commentCount = comments.filter((comment) => !comment.deletedAt && comment.status === 'open').length;
  },
  async loadSceneSegmentContent(this: WorkspaceSceneEditStore, versionId: string) {
    const version = this.sceneSegments.flatMap((segment) => segment.versions).find((item) => item.id === versionId);
    if (!version) throw new Error('Fassung ist nicht mehr geöffnet.');
    if (version.contentLoaded !== false) return { contentDoc: version.contentDoc, fieldValues: version.fieldValues };
    const cached = sceneContentRequests.get(version);
    if (cached) return cached;
    const request = api<{ item: Pick<SceneSegmentVersion, 'contentDoc' | 'fieldValues'> }>(`/api/v1/scene-segment-versions/${versionId}/content`)
      .then((result) => result.item);
    sceneContentRequests.set(version, request);
    try { return await request; } catch (error) { sceneContentRequests.delete(version); throw error; }
  },
  async loadSceneSegmentComments(this: WorkspaceSceneEditStore, versionId: string) {
    const entity = this.activeEntity;
    const version = this.sceneSegments.flatMap((segment) => segment.versions).find((item) => item.id === versionId);
    if (!entity || !version || version.commentsLoaded) return;
    const pending = sceneCommentRequests.get(version);
    if (pending) return pending;
    const generation = sceneCommentGenerations.get(version) ?? 0;
    const request = (async () => {
      const result = await api<{ items: SceneSegmentComment[] }>(`/api/v1/scene-segment-versions/${versionId}/comments`);
      if (this.activeEntity !== entity || (sceneCommentGenerations.get(version) ?? 0) !== generation) return;
      this.setSceneSegmentVersionComments(versionId, result.items);
    })();
    sceneCommentRequests.set(version, request);
    try { await request; } finally { sceneCommentRequests.delete(version); }
  },
  async createSceneSegmentComment(this: WorkspaceSceneEditStore, versionId: string, body: string, anchor: { from: number; to: number; text: string }) {
    const result = await api<{ items: SceneSegmentComment[] }>(`/api/v1/scene-segment-versions/${versionId}/comments`, { method: 'POST', body: JSON.stringify({ body, anchor }) });
    this.setSceneSegmentVersionComments(versionId, result.items);
  },
  async replyToSceneSegmentComment(this: WorkspaceSceneEditStore, commentId: string, body: string) {
    const versionId = this.sceneSegments.flatMap((segment) => segment.versions).find((version) => version.comments.some((comment) => comment.id === commentId))?.id;
    const result = await api<{ items: SceneSegmentComment[] }>(`/api/v1/scene-segment-comments/${commentId}/replies`, { method: 'POST', body: JSON.stringify({ body }) });
    if (versionId) this.setSceneSegmentVersionComments(versionId, result.items);
  },
  async setSceneSegmentCommentStatus(this: WorkspaceSceneEditStore, commentId: string, status: 'open' | 'resolved') {
    const versionId = this.sceneSegments.flatMap((segment) => segment.versions).find((version) => version.comments.some((comment) => comment.id === commentId))?.id;
    const result = await api<{ items: SceneSegmentComment[] }>(`/api/v1/scene-segment-comments/${commentId}/status`, { method: 'PATCH', body: JSON.stringify({ status }) });
    if (versionId) this.setSceneSegmentVersionComments(versionId, result.items);
  },
  async updateSceneSegmentComment(this: WorkspaceSceneEditStore, commentId: string, body: string) {
    const versionId = this.sceneSegments.flatMap((segment) => segment.versions).find((version) => version.comments.some((comment) => comment.id === commentId))?.id;
    const result = await api<{ items: SceneSegmentComment[] }>(`/api/v1/scene-segment-comments/${commentId}`, { method: 'PATCH', body: JSON.stringify({ body }) });
    if (versionId) this.setSceneSegmentVersionComments(versionId, result.items);
  },
  async deleteSceneSegmentComment(this: WorkspaceSceneEditStore, commentId: string) {
    const versionId = this.sceneSegments.flatMap((segment) => segment.versions).find((version) => version.comments.some((comment) => comment.id === commentId))?.id;
    const result = await api<{ items: SceneSegmentComment[] }>(`/api/v1/scene-segment-comments/${commentId}`, { method: 'DELETE' });
    if (versionId) this.setSceneSegmentVersionComments(versionId, result.items);
  },
  async previewSceneSegmentCommentAiPrompt(this: WorkspaceSceneEditStore, commentId: string) {
    return api<{ prompt: string; model: 'gpt-5.6-luna' }>(`/api/v1/scene-segment-comments/${commentId}/ai-prompt`);
  },
  async applySceneSegmentCommentWithAi(this: WorkspaceSceneEditStore, commentId: string) {
    const result = await api<{ item: SceneSegmentVersion; model: 'gpt-5.6-luna'; usage: { inputTokens: number; outputTokens: number; totalTokens: number } | null }>(`/api/v1/scene-segment-comments/${commentId}/apply-ai`, { method: 'POST' });
    await this.loadSceneSegments();
    await this.refreshActiveVersionMetadata();
    this.scheduleStoryTimelineRefresh();
    return result;
  },
  async updateSceneSegmentFields(this: WorkspaceSceneEditStore, fields: SceneSegmentField[]) {
    if (!this.activeEntity) return;
    await api(`/api/v1/entities/${this.activeEntity.id}/scene-segment-fields`, { method: 'PUT', body: JSON.stringify({ fields }) });
    await this.loadSceneSegments();
    await this.refreshActiveVersionMetadata();
    this.scheduleStoryTimelineRefresh();
  },
  async createSceneSegmentField(this: WorkspaceSceneEditStore, input: { segmentId: string; key: string; label: string; kind: SceneSegmentField['kind']; applyTo: 'this' | 'all' }) {
    if (!this.activeEntity) return;
    await api(`/api/v1/entities/${this.activeEntity.id}/scene-segment-fields`, { method: 'POST', body: JSON.stringify(input) });
    await this.loadSceneSegments();
    await this.refreshActiveVersionMetadata();
    this.scheduleStoryTimelineRefresh();
  },
  async deleteSceneSegmentField(this: WorkspaceSceneEditStore, fieldId: string) {
    await api(`/api/v1/scene-segment-fields/${fieldId}`, { method: 'DELETE' });
    await this.loadSceneSegments();
    await this.refreshActiveVersionMetadata();
    this.scheduleStoryTimelineRefresh();
  },
  async deleteSceneSegment(this: WorkspaceSceneEditStore, id: string) {
    await api(`/api/v1/scene-segments/${id}`, { method: 'DELETE' });
    await this.loadSceneSegments();
    await this.refreshActiveVersionMetadata();
    this.scheduleStoryTimelineRefresh();
  }
};
